import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useOrganizationEntitlements } from '@/hooks/useFeatureLimits';
import { usePendingOnboardingAssets } from '@/hooks/usePendingOnboardingAssets';
import { Loader2 } from 'lucide-react';

interface FeatureRouteProps {
  featureKey: string | null; // null means always accessible
  children: React.ReactNode;
  fallback?: string; // path to redirect to if feature is not enabled
  /**
   * Second feature that ALSO grants access. For routes that host more than one
   * feature — `/home/integrations` carries both the integrations grid and the
   * Social accounts tab (`social_media_posts`), so an org entitled to
   * only one of them must still be able to open the page.
   */
  alsoAllowFeatureKey?: string;
}

/**
 * FeatureRoute component - Protects routes based on feature enablement
 * Redirects to fallback path if feature is not enabled
 * 
 * @example
 * <Route path="contacts" element={
 *   <FeatureRoute featureKey="contacts" fallback="/home">
 *     <Contacts />
 *   </FeatureRoute>
 * } />
 */
export const FeatureRoute: React.FC<FeatureRouteProps> = ({
  featureKey,
  children,
  fallback = '/home',
  alsoAllowFeatureKey,
}) => {
  const location = useLocation();
  const { data: entitlements, isLoading } = useOrganizationEntitlements();
  const { data: pendingAssets, isLoading: pendingAssetsLoading } = usePendingOnboardingAssets();
  const enabledFeatures = entitlements?.entitlements?.features || {};

  // Always allow if no feature key (e.g., Overview, Settings)
  if (!featureKey) {
    return <>{children}</>;
  }

  // Show loading state while checking entitlements
  if (isLoading || pendingAssetsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  // Map child feature keys to their parent plan feature
  const childToParent: Record<string, string> = {
    // Commerce sub-features
    orders: 'commerce',
    products: 'commerce',
    abonded_carts: 'commerce',
    payments: 'commerce',
    store_reports: 'commerce',
    // Appointments sub-features
    scheduling: 'appointments',
    time_slots: 'appointments',
    meeting_links: 'appointments',
    whatsapp_reminders: 'appointments',
  };

  // Check if feature is enabled (directly or via parent)
  const isFeatureOn = (key: string) =>
    enabledFeatures[key] === true ||
    (childToParent[key] && enabledFeatures[childToParent[key]] === true);

  const isEnabled = isFeatureOn(featureKey) ||
    (!!alsoAllowFeatureKey && isFeatureOn(alsoAllowFeatureKey));

  const hasPendingTemplates = Array.isArray(pendingAssets?.pending_templates) && pendingAssets.pending_templates.length > 0;
  const hasPendingFlow = !!pendingAssets?.pending_flow;
  const hasActivatedOnboardingDrafts = !!pendingAssets?.activated_business_account_id;
  const isTemplatesRoute = location.pathname.startsWith('/home/templates');
  const isAutomationRoute = location.pathname.startsWith('/home/automation');
  const isBusinessAccountsRoute = location.pathname.startsWith('/home/business-accounts');
  const allowOnboardingAccess =
    (isTemplatesRoute && (hasPendingTemplates || hasActivatedOnboardingDrafts)) ||
    (isAutomationRoute && (hasPendingFlow || hasActivatedOnboardingDrafts)) ||
    (isBusinessAccountsRoute && (hasPendingTemplates || hasPendingFlow || hasActivatedOnboardingDrafts));

  if (!isEnabled && !allowOnboardingAccess) {
    return <Navigate to={fallback} replace />;
  }

  return <>{children}</>;
};
